import React, { useCallback, useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/joy";
import { theme } from "styles/theme";
import type { TaskEditorState } from "./utils/taskEditorUtils";
import TaskEditorLivePreview from "./TaskEditorLivePreview";
import TaskEditorWorkspace from "./TaskEditorWorkspace";
import TaskEditorCodeView from "./TaskEditorCodeView";
import { taskEditorScrollbarSx } from "./utils/taskEditorScrollbarSx";
import CustomModal from "components/common/CustomModal";
import UndoIcon from "@mui/icons-material/Undo";

interface TaskWorkspacePanelProps {
  editorState?: TaskEditorState;
  onChangeEditorState: (next: TaskEditorState) => void;
  hasUnsavedChanges: boolean;
  isCommitting: boolean;
  onCommit: () => void;
  onEditMeta: () => void;
  // hasUnsavedChanges=true 일 때만 의미. working vs V1 비교·복원에 사용.
  v1Task?: TaskEditorState;
  onDiscardWorking?: () => void | Promise<void>;
}

type WorkspaceTab = "edit" | "preview" | "code";

const TAB_LABELS: Record<WorkspaceTab, string> = {
  edit: "편집",
  preview: "미리보기",
  code: "코드",
};

const KEY_BADGE_COLOR = "#F59E0B";

export default function TaskWorkspacePanel({
  editorState,
  onChangeEditorState,
  hasUnsavedChanges,
  isCommitting,
  onCommit,
  onEditMeta,
  v1Task,
  onDiscardWorking,
}: TaskWorkspacePanelProps) {
  const [tab, setTab] = useState<WorkspaceTab>("edit");
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  // 미리보기에서 블록을 눌러 편집 탭으로 넘어갈 때, 포커스할 블록 index(0-based).
  // 편집 워크스페이스가 마운트되며 이 값을 소비하고 onFocusConsumed 로 비운다.
  const [pendingFocusBlockIndex, setPendingFocusBlockIndex] = useState<
    number | null
  >(null);

  const handleEditBlock = useCallback((blockIndex: number) => {
    setTab("edit");
    setPendingFocusBlockIndex(blockIndex);
  }, []);

  const handleFocusConsumed = useCallback(() => {
    setPendingFocusBlockIndex(null);
  }, []);

  if (!editorState) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: theme.vars.palette.Content.Weak,
          p: "32px",
        }}
      >
        <Typography level="body-md" sx={{ mb: "4px" }}>
          활동지를 선택하거나 만드세요
        </Typography>
        <Typography
          level="body-xs"
          sx={{ color: theme.vars.palette.Content.Weak }}
        >
          좌측 목록에서 활동지를 선택하거나 [+ 새 활동지] 버튼을 눌러
          시작하세요.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        backgroundColor: theme.vars.palette.Background.Level1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          px: "12px",
          py: "8px",
          borderBottom: `1px solid ${theme.vars.palette.Border.Weak}`,
          backgroundColor: theme.vars.palette.Surface.Forward,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ minWidth: 0, flexWrap: "nowrap" }}
          >
            <Typography
              level="title-sm"
              sx={{
                fontSize: "13px",
                fontWeight: 700,
                color: theme.vars.palette.Content.Strong,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                minWidth: 0,
              }}
            >
              {editorState.title || "(제목 없음)"}
            </Typography>
            {editorState.is_key_activity && (
              <Typography
                level="body-xs"
                sx={{
                  color: KEY_BADGE_COLOR,
                  fontSize: "12px",
                  flexShrink: 0,
                }}
                title="Key 활동지"
              >
                ★
              </Typography>
            )}
            <Typography
              level="body-xs"
              sx={{
                color: theme.vars.palette.Content.Medium,
                fontSize: "11px",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {editorState.task_id || "(ID 없음)"} ·{" "}
            </Typography>
            {editorState.set_number !== undefined && (
              <Typography
                level="body-xs"
                sx={{
                  color: theme.vars.palette.Content.Weak,
                  fontSize: "11px",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                세트 {editorState.set_number}
              </Typography>
            )}
            <Box
              className="meta-edit-btn"
              onClick={onEditMeta}
              sx={{
                flexShrink: 0,
                display: "inline-flex",
                alignItems: "center",
                lineHeight: 0,
                color: theme.vars.palette.Content.Weak,
                cursor: "pointer",
                px: "2px",
                "&:hover": {
                  color: theme.vars.palette.Content.Strong,
                },
              }}
              title="메타데이터 편집"
            >
              <span
                className="material-symbols-outlined"
                style={{
                  fontSize: "16px",
                }}
              >
                more_vert
              </span>
            </Box>
          </Stack>
        </Box>

        {hasUnsavedChanges && (
          <>
            <Box
              component="button"
              type="button"
              onClick={() => setDiscardConfirmOpen(true)}
              disabled={!v1Task || isCommitting || !onDiscardWorking}
              aria-label="원복"
              title="원복"
              sx={{
                background: "none",
                border: "none",
                padding: 0,
                margin: 0,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                lineHeight: 0,
                mr: "8px",
                color: theme.vars.palette.Content.Weak,
                "&:hover": { color: theme.vars.palette.Content.Medium },
                "&:disabled": {
                  color: theme.vars.palette.Content.Weak,
                  opacity: 0.4,
                  cursor: "default",
                },
              }}
            >
              <UndoIcon sx={{ fontSize: "18px" }} />
            </Box>
          </>
        )}
        <Button
          size="sm"
          variant="Solid"
          color="primary"
          onClick={onCommit}
          disabled={!hasUnsavedChanges || isCommitting}
          loading={isCommitting}
          sx={{ fontSize: "12px", minHeight: "28px" }}
        >
          {"커밋"}
        </Button>
      </Box>

      <CustomModal
        open={discardConfirmOpen}
        onClose={() => setDiscardConfirmOpen(false)}
        title="변경 내용을 원복할까요?"
        subtitle="커밋되지 않은 변경 내용이 모두 사라지고 마지막 커밋 상태로 되돌아갑니다."
        actions={[
          {
            label: "원복하기",
            variant: "Solid",
            color: "danger",
            onClick: () => {
              setDiscardConfirmOpen(false);
              void onDiscardWorking?.();
            },
          },
          {
            label: "취소",
            variant: "Plain",
            onClick: () => setDiscardConfirmOpen(false),
          },
        ]}
      />

      <Box
        sx={{
          display: "flex",
          gap: "4px",
          px: "12px",
          pt: "6px",
          borderBottom: `1px solid ${theme.vars.palette.Border.Weak}`,
          backgroundColor: theme.vars.palette.Surface.Forward,
        }}
      >
        {(["edit", "preview", "code"] as const).map((tabId) => {
          const isActive = tab === tabId;
          return (
            <Box
              key={tabId}
              onClick={() => setTab(tabId)}
              sx={{
                px: "10px",
                py: "6px",
                cursor: "pointer",
                borderBottom: `2px solid ${
                  isActive ? theme.vars.palette.Content.Strong : "transparent"
                }`,
                color: isActive
                  ? theme.vars.palette.Content.Strong
                  : theme.vars.palette.Content.Weak,
                fontWeight: isActive ? 700 : 500,
                fontSize: "12px",
                userSelect: "none",
              }}
            >
              {TAB_LABELS[tabId]}
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          ...taskEditorScrollbarSx,
        }}
      >
        {tab === "edit" && (
          <TaskEditorWorkspace
            editorState={editorState}
            onChangeEditorState={onChangeEditorState}
            focusBlockIndex={pendingFocusBlockIndex}
            onFocusConsumed={handleFocusConsumed}
          />
        )}
        {tab === "preview" && (
          <Box sx={{ px: "12px", py: "8px" }}>
            <TaskEditorLivePreview
              task={editorState}
              onEditBlock={handleEditBlock}
            />
          </Box>
        )}
        {tab === "code" && (
          <Box
            sx={{
              px: "9%",
              pt: "8px",
              pb: "12px",
              height: "100%",
              minHeight: 0,
            }}
          >
            <TaskEditorCodeView task={editorState} v1Task={v1Task} />
          </Box>
        )}
      </Box>
    </Box>
  );
}
